import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import type { Lesson, LessonDrillRef, OfferingType } from '../types/lesson';
import type { Drill } from '../types/lesson';
import { VideoEditorPlayer, type VideoEditorPlayerRef } from './VideoEditorPlayer';
import { Timeline } from './Timeline';
import type { NotableSegment, VoiceoverNotableSegmentClip } from '../types/videoEdit';
import { ClipEditorModal, type ClipSource } from './ClipEditorModal';
import { SegmentCaptureModal } from './SegmentCaptureModal';
import { useScrubPause } from '../hooks/useScrubPause';
import {
  EBTN_PRIMARY,
  EBTN_GHOST,
  EBTN_ICON,
  EBTN_SECONDARY,
  EBTN_DESTRUCTIVE,
  EBTN_ICON_ACTIVE,
} from './videoEditor/editorButtonStyles';
import { LessonOrganizerPanel } from './LessonOrganizerPanel';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthContext';
import { syncSegmentsToPlayerLibrary } from '../services/playerLibrary';
import { AddComparisonSourceModal } from './AddComparisonSourceModal';
import ProgressReportModal from './ProgressReportModal';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Draft data that can be saved/loaded.
 * Legacy fields (recordingSegments, annotationSnapshots, audioRecordings, clipMasks)
 * are intentionally omitted — the new model uses only voiceover clip sequences.
 */
export interface VideoEditorDraftData {
  notableSegments: NotableSegment[];
  voiceoverNotableSegmentClips: VoiceoverNotableSegmentClip[];
  notes: string;
  /** UI preference: mute original video while scrubbing. NOT sent to cloud-run. */
  audioMuted: boolean;
  /** Drill snapshots the coach has attached to this lesson */
  attachedDrills?: LessonDrillRef[];
  /** Private coach-only notes — never displayed in player-facing views */
  coachNotes?: string;
  /** Clockwise rotation applied to the player video (0 | 90 | 180 | 270). Default: 0. */
  videoRotation?: number;
}

interface LessonVideoEditorProps {
  lessonRequest?: Lesson;
  videoUrl?: string;

  title?: string;
  /**
   * Explicit second line. No production caller passes one: the auto-generated
   * "Lesson #xxxxxxxx" that used to fill it is gone, since a truncated document
   * id told the coach nothing. Kept because two dev harnesses set it, and it is
   * the admin page among them.
   */
  subtitle?: string;

  onCancel: () => void;
  /** Auto-called on changes. Should upload blobs and return data with storage paths merged in. */
  onSaveDraft?: (data: VideoEditorDraftData) => Promise<VideoEditorDraftData>;
  /** Called when user clicks "Submit lesson" */
  onSubmitDraft?: (data: VideoEditorDraftData) => Promise<void>;

  initialDraft?: VideoEditorDraftData;

  /** Player ID — when set, enables "Save to player library" on timeline segments */
  playerId?: string;
  /** Lesson ID — recorded on saved segments for cross-lesson traceability */
  lessonId?: string;
  /** Original video ID — stored on saved segments to reconstruct the video URL later */
  originalVideoId?: string;
  /** Offering type — drives editor copy (title + organizer length hint). Optional; omitting falls back to "Record Lesson" / ~16min defaults. */
  offeringType?: OfferingType;
  /** Fires after a successful submission, after a brief success-state flash. Use to navigate, toast, etc. */
  onSubmitSuccess?: (lessonId: string, playerName?: string) => void;
  /** True when this lesson is the final program lesson (offering=training_program AND lessonNumber===totalLessons).
   *  When true: the submit button is relabeled, opens the ProgressReportModal instead of the standard submit
   *  confirmation, and the organizer panel renders the priming pill + microcopy. */
  isFinalProgramLesson?: boolean;
  /** Player first name — used in the ProgressReportModal headline + question personalization +
   *  organizer-panel microcopy. Falls back to "player" inside the modal when omitted. */
  playerFirstName?: string;
  /** Total program lessons — drives the modal's journey strip dot count. Defaults to 4 when omitted. */
  totalLessons?: number;
  /** Player's full display name, resolved by the host. Used for the header title
   *  and the details popover. Omitting it falls the title back to the lesson type. */
  playerName?: string;
  /** Player's photo, resolved by the host from playerProfile then the order. */
  playerAvatarUrl?: string;
  /** Player's UTR, if they have one. */
  playerUtr?: number;
  /** Player's stated goal (from playerProfile.goal). Surfaced as a small reference card
   *  at the top of the ProgressReportModal so the coach can write toward it. Optional. */
  playerGoal?: string;
  /**
   * Final-lesson submit handler. Owns the lesson submit + report write
   * pipeline in the parent (lesson submit first, then report). Throws
   * ONLY on lesson-side failure so the modal can stay open for retry.
   * Resolves on lesson success regardless of report success — parent
   * handles the partial-failure toast.
   */
  onFinalSubmit?: (params: {
    draftData: VideoEditorDraftData;
    report: { whatImproved: string; keepDoing: string; nextFocus: string; personalNote: string };
  }) => Promise<void>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

// Number of editors currently mounted. A counter rather than a boolean so a
// second editor mounting before the first unmounts (a remount during a route
// change, say) cannot clear the flag early — the attribute is removed only
// when the last one goes. Module scope: shared by every instance.
let openEditorCount = 0;

const AUTOSAVE_DEBOUNCE_MS = 1500;

/**
 * A fingerprint of a draft, used only to answer "is what is on screen the same
 * as what was last written?".
 *
 * Key order is normalised because clips make a round trip through Firestore and
 * come back in whatever order it chose. Without sorting, an unchanged draft
 * would fingerprint differently after every save, schedule another one, and
 * loop.
 *
 * Blobs are reduced to size and type: JSON.stringify turns a Blob into `{}`, so
 * two different recordings would otherwise look identical. Their clip ids
 * already differ, so this is belt and braces rather than the only guard.
 *
 * undefined drops out, which is what we want — Firestore does not store it, so
 * an absent field and an explicit undefined are the same write.
 */
function draftKey(d: VideoEditorDraftData): string {
  return JSON.stringify(d, (_k, v) => {
    if (v instanceof Blob) return `blob:${v.size}:${v.type}`;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v as Record<string, unknown>).sort()) {
        out[k] = (v as Record<string, unknown>)[k];
      }
      return out;
    }
    return v;
  });
}

export function LessonVideoEditor({
  lessonRequest,
  videoUrl: directVideoUrl,
  title,
  subtitle,
  onCancel,
  onSaveDraft,
  onSubmitDraft,
  initialDraft,
  playerId,
  lessonId,
  originalVideoId,
  offeringType,
  onSubmitSuccess,
  isFinalProgramLesson = false,
  playerFirstName,
  playerName,
  playerAvatarUrl,
  playerUtr,
  totalLessons,
  playerGoal,
  onFinalSubmit,
}: LessonVideoEditorProps) {
  const videoUrl = directVideoUrl || lessonRequest?.playerVideoUrl || '';
  /**
   * The lesson type on its own, reused for the title and as the fallback when
   * the host has no name for the player yet.
   */
  const typeLabel = offeringType === 'shot_consult' ? 'Shot Consult' : 'Lesson';
  /** First name only. The title is a sentence, not a record. */
  const titleFirstName = (playerFirstName || playerName || '').trim().split(' ')[0];
  const displayTitle = title || typeLabel;

  const videoPlayerRef = useRef<VideoEditorPlayerRef>(null);

  /** Player details popover, opened from the name in the title. */
  const [playerCardOpen, setPlayerCardOpen] = useState(false);
  const [playerCardAnchor, setPlayerCardAnchor] = useState<{ x: number; y: number } | null>(null);
  const playerChipRef = useRef<HTMLButtonElement>(null);
  const playerCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!playerCardOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (playerCardRef.current?.contains(t) || playerChipRef.current?.contains(t)) return;
      setPlayerCardOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPlayerCardOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [playerCardOpen]);

  // ── Core state ───────────────────────────────────────────────────────────────
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoRotation, setVideoRotation] = useState<0 | 90 | 180 | 270>(0);

  // ── Notable segments ─────────────────────────────────────────────────────────
  const [notableSegments, setNotableSegments] = useState<NotableSegment[]>([]);

  // Naming modal
  /** Bounds of the pending capture, or null when the modal is closed. */
  const [pendingSegmentBounds, setPendingSegmentBounds] = useState<{ startTime: number; endTime: number } | null>(null);
  /**
   * Where the coach last dragged the capture panel. Editor state rather than
   * localStorage, so it holds across captures within a session and resets when
   * the editor is closed and reopened.
   */
  const [capturePanelPos, setCapturePanelPos] = useState<{ x: number; y: number } | null>(null);
  const [segmentNameInput, setSegmentNameInput] = useState('');

  // ── Voiceover clips (in sequence order) ──────────────────────────────────────
  const [voiceoverClips, setVoiceoverClips] = useState<VoiceoverNotableSegmentClip[]>([]);

  // ── Notable segment delete confirmation ───────────────────────────────────────
  const [showNotableSegDeleteConfirm, setShowNotableSegDeleteConfirm] = useState<{ segmentId: string; clipCount: number } | null>(null);

  // ── Clip editor modal ─────────────────────────────────────────────────────────
  const [clipEditorPrimarySource, setClipEditorPrimarySource] = useState<ClipSource | null>(null);
  const [clipEditorSecondarySource, setClipEditorSecondarySource] = useState<ClipSource | null>(null);
  const [showClipEditorModal, setShowClipEditorModal] = useState(false);
  const [showComparisonSourceModal, setShowComparisonSourceModal] = useState(false);

  /* Pause the lesson video when either editor modal opens.
     It used to keep playing behind them — audible, and still burning decode
     while the coach worked in a dialog that covers it.

     One effect rather than a call at each of the eight places that open these
     modals (Add Recording, Side-by-Side, the per-segment entry points, the
     comparison-source confirm path). Keying on the flags means a ninth entry
     point added later is covered without anyone remembering to.

     Deliberately no resume on close. The coach opened a dialog to do something
     else and has been looking at a different video; restarting playback from a
     position they left several minutes ago, possibly with sound, is a surprise
     rather than a convenience. Play is one click and it is their call. */
  useEffect(() => {
    if (!showClipEditorModal && !showComparisonSourceModal && !pendingSegmentBounds) return;
    // pause() is idempotent, so no need to check whether it is playing first.
    videoPlayerRef.current?.pause();
  }, [showClipEditorModal, showComparisonSourceModal, pendingSegmentBounds]);

  /* Scrub-to-pause for the organizer's transport.
     Timeline reports the drag edges; the decision and the video live here,
     which is why the pause is not inside Timeline. Same rule as every other
     scrubber in the app: resume only if it was running when the drag began,
     so scrubbing a paused video to hunt for a frame leaves it paused. */
  const { begin: beginTransportScrub, end: endTransportScrub } = useScrubPause(
    () => videoPlayerRef.current?.getVideoElement() ?? null,
  );
  /**
   * Undefined means "open on the source list".
   *
   * It used to default to 'current', which is never absent — so the picker
   * always opened on a source. Worse, inside the recorder the current-video
   * list is usually empty (its only segment is the one being recorded), and
   * the modal's resolveTab falls through from an empty 'current' to 'player'.
   * That is why the recorder's Side-by-Side landed on From Previous Lesson
   * with the other five sources unreachable.
   */
  const [comparisonDefaultTab, setComparisonDefaultTab] =
    useState<'current' | 'player' | 'local' | 'camera' | 'drill-library' | 'pro-library' | undefined>(undefined);
  const openClipEditorAfterComparisonRef = useRef(false);

  // ── Upload file ref (for side-by-side upload flow) ────────────────────────────
  const uploadFileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadSegmentIdRef = useRef<string | null>(null);

  const [notes, setNotes] = useState('');
  const [coachNotes, setCoachNotes] = useState('');
  const [attachedDrills, setAttachedDrills] = useState<LessonDrillRef[]>([]);

  // ── Preview state ─────────────────────────────────────────────────────────────
  const [previewClipId, setPreviewClipId] = useState<string | null>(null);
  const [previewClipUrl, setPreviewClipUrl] = useState<string | null>(null);
  const [previewClipLoading, setPreviewClipLoading] = useState(false);
  const [previewLessonOpen, setPreviewLessonOpen] = useState(false);
  const [previewLessonIdx, setPreviewLessonIdx] = useState(0);
  const [previewLessonUrl, setPreviewLessonUrl] = useState<string | null>(null);

  const handleToggleDrill = useCallback((drill: Drill) => {
    setAttachedDrills((prev) => {
      const exists = prev.some((d) => d.drillId === drill.id);
      if (exists) return prev.filter((d) => d.drillId !== drill.id);
      const ref: LessonDrillRef = {
        drillId: drill.id,
        videoUrl: drill.videoUrl,
        videoStoragePath: drill.videoStoragePath,
        title: drill.title,
        description: drill.description,
        shotType: drill.shotType,
        // Omit undefined optional fields — Firestore rejects undefined values
        ...(drill.thumbnailUrl && { thumbnailUrl: drill.thumbnailUrl }),
        ...(drill.durationSeconds !== undefined && { durationSeconds: drill.durationSeconds }),
      };
      return [...prev, ref];
    });
  }, []);

  // Lock body scroll while the editor is open so the scrollbar disappears
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────────
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  /** Mirrors organizerOpen for the effect below, which must not re-run on it. */
  const organizerOpenRef = useRef(false);
  /** The rail's state before the clip editor took the width, or null when closed. */
  const organizerBeforeClipRef = useRef<boolean | null>(null);

  const [organizerOpen, setOrganizerOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Brief post-submit success flash on the header button (~1.2s) before
  // onSubmitSuccess fires and the parent navigates away.
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // ── Final-lesson progress-report modal ────────────────────────────────────────
  // Draft state for the four progress-report fields lives HERE (not inside
  // ProgressReportModal) so the "Save draft" close + reopen flow within the
  // same editor session preserves the answers. The modal is a controlled
  // component that receives values + setters as props. Draft state evaporates
  // when the editor unmounts (intentional session boundary — no localStorage,
  // no backend persistence).
  const [progressReportOpen, setProgressReportOpen] = useState(false);
  const [progressReportDraft, setProgressReportDraft] = useState({
    whatImproved: '',
    keepDoing: '',
    nextFocus: '',
    personalNote: '',
  });

  // ── Auto-save ─────────────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  /** True when what is on screen differs from what was last written. */
  const [isDirty, setIsDirty] = useState(false);
  /**
   * Fingerprint of the last draft actually written, or null to adopt whatever
   * is in state as the baseline without writing it.
   *
   * This replaces a countdown of renders to skip. The counter could not tell
   * "this render is the echo of my own save" from "this render is the coach
   * adding a segment", so any edit made during a round trip was swallowed by
   * the skip the response queued — and the effect cleanup cancelled its timer
   * on the way past. Comparing against what was written cannot confuse the two.
   */
  const lastSavedKeyRef = useRef<string | null>(null);
  const isSavingRef = useRef(false);
  /** Set when a save was wanted while one was already in flight. */
  const pendingSaveRef = useRef(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSaveDraftRef = useRef(onSaveDraft);
  onSaveDraftRef.current = onSaveDraft;
  const buildDraftDataRef = useRef<(() => VideoEditorDraftData) | null>(null);
  const runSaveRef = useRef<(() => Promise<void>) | null>(null);

  const { showSuccess, showError, showInfo } = useToast();
  const { currentUser } = useAuth();

  // ── Initialize from draft ─────────────────────────────────────────────────────
  useEffect(() => {
    if (initialDraft) {
      /* Drop the baseline so the next auto-save pass adopts the freshly loaded
         draft rather than writing it straight back. Previously this added 7 to
         a skip counter, one per setState "in case React doesn't batch them" —
         React 19 does batch them, so six of the seven were left over and each
         swallowed one of the coach's next six edits. */
      lastSavedKeyRef.current = null;
      setNotableSegments(initialDraft.notableSegments ?? []);
      setVoiceoverClips((current) => {
        // Never overwrite clips the coach has already recorded. The parent can
        // call setLoadedDraftData asynchronously (after the editor is already
        // open and recording has happened), and the draft it found was saved
        // before the clip was recorded — applying it would silently drop the clip.
        const hasRecorded = current.some((c) => c.videoBlob || c.videoStoragePath);
        if (hasRecorded) return current;
        return initialDraft.voiceoverNotableSegmentClips ?? [];
      });
      setNotes(initialDraft.notes ?? '');
      setCoachNotes(initialDraft.coachNotes ?? '');
      setAudioMuted(initialDraft.audioMuted ?? false);
      setAttachedDrills(initialDraft.attachedDrills ?? []);
      setVideoRotation((initialDraft.videoRotation as 0 | 90 | 180 | 270) ?? 0);
    }
  }, [initialDraft]);

  /** Arms the debounce. Always clears first, so this is the only writer of the timer. */
  const armSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => { void runSaveRef.current?.(); }, AUTOSAVE_DEBOUNCE_MS);
  }, []);

  const runSave = useCallback(async () => {
    const save = onSaveDraftRef.current;
    const build = buildDraftDataRef.current;
    if (!save || !build) return;
    if (isSavingRef.current) {
      /* Do not drop it. The previous version returned here and nothing ever
         rescheduled, so a save that collided with one in flight was lost
         outright. */
      pendingSaveRef.current = true;
      return;
    }
    isSavingRef.current = true;
    setSaveStatus('saving');
    const sent = build();
    try {
      const updatedData = await save(sent);
      /* The baseline is what was written, with the server's copies of the clips
         that were sent — not the post-merge state. Anything added during the
         round trip is therefore still a difference and still gets written. */
      lastSavedKeyRef.current = draftKey({
        ...sent,
        voiceoverNotableSegmentClips: updatedData.voiceoverNotableSegmentClips,
      });
      // Merge server response with current state: for each clip still present
      // locally, swap in the server version (which carries the storage path).
      // Clips deleted while the save was in flight are not in `current` and
      // must not be re-added — driving the merge from `current` enforces that.
      setVoiceoverClips((current) => {
        const serverById = new Map(
          updatedData.voiceoverNotableSegmentClips.map((c) => [c.id, c])
        );
        return current.map((c) => serverById.get(c.id) ?? c);
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus((st) => (st === 'saved' ? 'idle' : st)), 3000);
      // Sync notable segments to player library (silent — no toast)
      if (playerId && currentUser && sent.notableSegments.length > 0) {
        const ctx = lessonId ? { type: 'lesson' as const, id: lessonId } : { type: 'admin' as const };
        syncSegmentsToPlayerLibrary(
          sent.notableSegments, playerId, ctx, originalVideoId ?? '',
          currentUser.uid, currentUser.displayName ?? '', lessonId,
        ).catch((err) => console.error('[playerLibrary sync]', err)); // fire-and-forget
      }
    } catch {
      setSaveStatus('error');
    } finally {
      isSavingRef.current = false;
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        armSave();
      }
    }
  }, [armSave, playerId, currentUser, lessonId, originalVideoId]);
  runSaveRef.current = runSave;

  // ── Auto-save trigger ─────────────────────────────────────────────────────────
  useEffect(() => {
    // Don't auto-save while the clip recording modal is open — the clip blob lives
    // only in ClipEditorModal state at this point and hasn't been handed back via
    // onSave yet. Saving now would omit the clip; when the modal closes the dep
    // change re-runs this effect with the clip already in voiceoverClips.
    if (showClipEditorModal) return;
    const build = buildDraftDataRef.current;
    if (!onSaveDraftRef.current || !build) return;
    const key = draftKey(build());
    if (lastSavedKeyRef.current === null) {
      // Nothing written yet this session: adopt what is here rather than writing it back.
      lastSavedKeyRef.current = key;
      setIsDirty(false);
      return;
    }
    if (key === lastSavedKeyRef.current) {
      setIsDirty(false);
      return;
    }
    setIsDirty(true);
    armSave();
    /* Deliberately no cleanup. A cleanup that cancels the timer runs before the
       *next* effect pass whatever that pass decides, which is how the echo of a
       completed save used to cancel a write queued behind it. armSave clears
       before it arms, so the debounce still collapses; the timer is cancelled
       only on unmount, below.

     The deps are exhaustive now: everything else the pass touches is read
     through a ref, so the disable the old version needed is gone. */
  }, [voiceoverClips, notes, coachNotes, notableSegments, audioMuted, attachedDrills, videoRotation, showClipEditorModal, armSave]);

  useEffect(() => () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
  }, []);

  /**
   * Best effort, and only that.
   *
   * `pagehide` fires on refresh, on tab close, and — unlike `beforeunload` — on
   * mobile Safari backgrounding, which is where a coach loses work. The write
   * cannot be awaited: the page may be gone before the request leaves. It
   * cannot be a sendBeacon either, because the write goes through the Firestore
   * SDK rather than a plain POST. So this closes the debounce window when the
   * page survives long enough (a backgrounded tab usually does) and does
   * nothing at all when it does not. It is a second chance, not a guarantee.
   */
  useEffect(() => {
    const onPageHide = () => {
      if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
      const save = onSaveDraftRef.current;
      const build = buildDraftDataRef.current;
      if (!save || !build || isSavingRef.current) return;
      const data = build();
      if (draftKey(data) === lastSavedKeyRef.current) return;
      void save(data).catch(() => {});
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  // ── Video controls ────────────────────────────────────────────────────────────
  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      videoPlayerRef.current?.pause();
    } else {
      videoPlayerRef.current?.play();
    }
  }, [isPlaying]);

  const handleSeek = useCallback((time: number) => {
    videoPlayerRef.current?.seek(time);
  }, []);

  // When set, the player auto-stops when currentTime reaches this value
  const playUntilRef = useRef<number | null>(null);

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time);
    if (playUntilRef.current !== null && time >= playUntilRef.current) {
      videoPlayerRef.current?.pause();
      playUntilRef.current = null;
    }
  }, []);

  const handlePlaySegment = useCallback((seg: NotableSegment) => {
    playUntilRef.current = seg.endTime;
    videoPlayerRef.current?.seek(seg.startTime);
    videoPlayerRef.current?.play();
  }, []);

  const handleToggleAudioMute = useCallback(() => {
    setAudioMuted((prev) => !prev);
  }, []);

  organizerOpenRef.current = organizerOpen;

  /**
   * Give the clip editor the full width, then hand the rail back exactly as it
   * was. Keyed on the clip editor alone: the coach can still reopen the rail
   * from the header while recording, and that must not be mistaken for the
   * state to restore.
   */
  useEffect(() => {
    if (showClipEditorModal) {
      if (organizerBeforeClipRef.current === null) {
        organizerBeforeClipRef.current = organizerOpenRef.current;
        setOrganizerOpen(false);
      }
    } else if (organizerBeforeClipRef.current !== null) {
      setOrganizerOpen(organizerBeforeClipRef.current);
      organizerBeforeClipRef.current = null;
    }
  }, [showClipEditorModal]);

  // ── Retroactive segment capture ───────────────────────────────────────────
  /**
   * One press, after the point has happened. The coach is watching, not
   * pre-marking, so the window is taken backwards from the playhead rather
   * than being bounded by two presses.
   */
  const CAPTURE_SECONDS = 15;
  const handleCaptureSegment = useCallback(() => {
    // Under a second in, there is nothing behind the playhead worth taking.
    if (currentTime < 1) {
      showInfo('Nothing to capture yet');
      return;
    }
    if (isPlaying) videoPlayerRef.current?.pause();
    setPendingSegmentBounds({
      startTime: Math.max(0, currentTime - CAPTURE_SECONDS),
      endTime: currentTime,
    });
    /* "Part", not "Segment". A coach sees this while working and a player sees
       it in the delivered lesson; "segment" is our word for it, not theirs.
       New segments only — saved ones keep whatever they were named. */
    setSegmentNameInput(`Part ${notableSegments.length + 1}`);
  }, [currentTime, isPlaying, notableSegments.length, showInfo]);

  const handleCancelCapture = useCallback(() => {
    setPendingSegmentBounds(null);
  }, []);

  const handleCreateHighlightFromInterval = useCallback((startTime: number, endTime: number) => {
    if (isPlaying) videoPlayerRef.current?.pause();
    setPendingSegmentBounds({ startTime, endTime });
    setSegmentNameInput(`Part ${notableSegments.length + 1}`);
  }, [isPlaying, notableSegments.length]);

  const handleSaveCapture = useCallback((startTime: number, endTime: number, label: string) => {
    const seg: NotableSegment = {
      id: `notable-seg-${Date.now()}`,
      label,
      startTime,
      endTime,
    };
    setNotableSegments((prev) => [...prev, seg].sort((a, b) => a.startTime - b.startTime));
    setPendingSegmentBounds(null);
    // Playback is not resumed: the coach decides when to carry on.
    showSuccess(`"${seg.label}" saved (${formatTime(startTime)} – ${formatTime(endTime)})`);
  }, [showSuccess]);


  /** Opens an existing segment directly in the Clip Editor (single-video recording) */
  const handleOpenSegmentInClipEditor = useCallback((seg: NotableSegment) => {
    setClipEditorPrimarySource({ videoUrl: videoUrl ?? '', segment: seg, label: seg.label, rotation: videoRotation || undefined });
    setClipEditorSecondarySource(null);
    setShowClipEditorModal(true);
  }, [videoUrl, videoRotation]);

  /** Called from ClipEditorModal's "Add video source" button — opens the dedicated comparison picker */
  const handleAddSecondarySourceToClipEditor = useCallback(() => {
    setComparisonDefaultTab(undefined);
    setShowComparisonSourceModal(true);
  }, []);

  // ── Update notable segment end time (from timeline drag) ──────────────────────
  // Frozen if segment is used in any clip — length is locked in that case
  const handleUpdateNotableSegmentEndTime = useCallback((segmentId: string, newEndTime: number) => {
    const isUsedInClip = voiceoverClips.some((c) => c.segmentIds.includes(segmentId));
    if (isUsedInClip) return;
    setNotableSegments((prev) =>
      prev.map((seg) => (seg.id === segmentId ? { ...seg, endTime: newEndTime } : seg))
    );
  }, [voiceoverClips]);

  // ── Update notable segment label/note (from Segments tab inline edit) ─────────
  const handleUpdateSegment = useCallback((segId: string, update: Partial<Pick<NotableSegment, 'label' | 'note'>>) => {
    setNotableSegments((prev) => {
      const idx = prev.findIndex((s) => s.id === segId);
      if (idx === -1) return prev; // not in this draft — no-op, keeps same reference
      return prev.map((seg) => (seg.id === segId ? { ...seg, ...update } : seg));
    });
  }, []);

  // ── Delete notable segment ────────────────────────────────────────────────────
  const handleDeleteNotableSegment = useCallback((segmentId: string) => {
    const clipCount = voiceoverClips.filter((c) => c.segmentIds.includes(segmentId)).length;
    if (clipCount > 0) {
      setShowNotableSegDeleteConfirm({ segmentId, clipCount });
    } else {
      setNotableSegments((prev) => prev.filter((s) => s.id !== segmentId));
      showInfo('Segment deleted');
    }
  }, [voiceoverClips, showInfo]);

  const handleConfirmDeleteNotableSegment = useCallback(() => {
    if (!showNotableSegDeleteConfirm) return;
    const { segmentId } = showNotableSegDeleteConfirm;
    setNotableSegments((prev) => prev.filter((s) => s.id !== segmentId));
    setVoiceoverClips((prev) => prev.filter((c) => !c.segmentIds.includes(segmentId)));
    setShowNotableSegDeleteConfirm(null);
    showInfo('Segment and associated clips deleted');
  }, [showNotableSegDeleteConfirm, showInfo]);


  // ── Clip management ───────────────────────────────────────────────────────────
  const handleSaveClip = useCallback((clip: VoiceoverNotableSegmentClip) => {
    setVoiceoverClips((prev) => [...prev, clip]);
    showSuccess('Clip saved!');
  }, [showSuccess]);

  const handleDeleteClip = useCallback((clipId: string) => {
    setVoiceoverClips((prev) => prev.filter((c) => c.id !== clipId));
    showInfo('Clip deleted');
  }, [showInfo]);

  const handleRenameClip = useCallback((clipId: string, newLabel: string) => {
    setVoiceoverClips((prev) => prev.map((c) => c.id === clipId ? { ...c, label: newLabel } : c));
  }, []);

  // ── Add recording for segment (opens Clip Editor with segment as primary) ──────
  const handleAddRecordingForSegment = useCallback((segmentId: string) => {
    const seg = notableSegments.find((s) => s.id === segmentId);
    if (!seg) return;
    handleOpenSegmentInClipEditor(seg);
  }, [notableSegments, handleOpenSegmentInClipEditor]);

  // ── Add side-by-side for segment ──────────────────────────────────────────────
  const handleAddSideBySideForSegment = useCallback((
    segmentId: string,
    sourceType: 'camera' | 'upload' | 'currentVideo' | 'playerLibrary' | 'drillLibrary' | 'proLibrary',
  ) => {
    const primarySeg = notableSegments.find((s) => s.id === segmentId);
    if (!primarySeg) return;
    const primary: ClipSource = { videoUrl: videoUrl ?? '', segment: primarySeg, label: primarySeg.label, rotation: videoRotation || undefined };

    if (sourceType === 'camera') {
      setClipEditorPrimarySource(primary);
      setClipEditorSecondarySource({ videoUrl: '', isCameraSource: true, label: 'Camera' });
      setShowClipEditorModal(true);
    } else if (sourceType === 'upload') {
      pendingUploadSegmentIdRef.current = segmentId;
      uploadFileInputRef.current?.click();
    } else if (sourceType === 'currentVideo') {
      setClipEditorPrimarySource(primary);
      setClipEditorSecondarySource(null);
      setComparisonDefaultTab('current');
      openClipEditorAfterComparisonRef.current = true;
      setShowComparisonSourceModal(true);
    } else if (sourceType === 'playerLibrary') {
      setClipEditorPrimarySource(primary);
      setClipEditorSecondarySource(null);
      setComparisonDefaultTab('player');
      openClipEditorAfterComparisonRef.current = true;
      setShowComparisonSourceModal(true);
    } else if (sourceType === 'drillLibrary' || sourceType === 'proLibrary') {
      setClipEditorPrimarySource(primary);
      setClipEditorSecondarySource(null);
      setComparisonDefaultTab(sourceType === 'drillLibrary' ? 'drill-library' : 'pro-library');
      openClipEditorAfterComparisonRef.current = true;
      setShowComparisonSourceModal(true);
    }
  }, [notableSegments, videoUrl, videoRotation]);

  // ── Reorder segments ──────────────────────────────────────────────────────────
  const handleReorderSegments = useCallback((reordered: NotableSegment[]) => {
    setNotableSegments(reordered);
    /**
     * Playback reads voiceoverClips in array order, so reordering the segment
     * list alone changed the rail and nothing else — the coach saw a new order
     * and the delivered lesson played the old one.
     *
     * Sorted here, at the drag, rather than derived from notableSegments at
     * read time. A delivered lesson plays from its saved array, and a coach may
     * have reordered before delivery, so deriving at read time would change
     * what an already-delivered lesson plays. Sorting on the drag only ever
     * affects the draft in front of the coach.
     *
     * Stable and partial: clips with no segment hold the index they already
     * occupy, because a camera recording was made at a point in the lesson and
     * a drag elsewhere is no reason to move it. Everything else is grouped by
     * segmentIds[0] in the new order, keeping recording order within a segment.
     */
    setVoiceoverClips((clips) => {
      const rank = new Map(reordered.map((seg, i) => [seg.id, i]));
      const movableIdx: number[] = [];
      clips.forEach((c, i) => { if (c.segmentIds[0] !== undefined) movableIdx.push(i); });
      const sorted = movableIdx
        .map((i) => ({ i, clip: clips[i] }))
        .sort((a, b) => {
          const ra = rank.get(a.clip.segmentIds[0]) ?? Number.MAX_SAFE_INTEGER;
          const rb = rank.get(b.clip.segmentIds[0]) ?? Number.MAX_SAFE_INTEGER;
          return ra !== rb ? ra - rb : a.i - b.i;   // ties keep recording order
        })
        .map((e) => e.clip);
      const next = [...clips];
      movableIdx.forEach((slot, k) => { next[slot] = sorted[k]; });
      return next;
    });
  }, []);

  // ── Draft data builder ────────────────────────────────────────────────────────
  const buildDraftData = useCallback((): VideoEditorDraftData => ({
    notableSegments,
    voiceoverNotableSegmentClips: voiceoverClips,
    notes,
    coachNotes,
    audioMuted,
    attachedDrills,
    videoRotation,
  }), [notableSegments, voiceoverClips, notes, coachNotes, audioMuted, attachedDrills, videoRotation]);
  buildDraftDataRef.current = buildDraftData;

  const hasClips = voiceoverClips.some((c) => c.videoBlob || c.videoStoragePath);

  // Clips that can actually be previewed (have video data)
  const previewableClips = voiceoverClips.filter((c) => c.videoBlob || c.videoStoragePath);

  // Resolve blob URL for the individual clip preview modal
  useEffect(() => {
    if (!previewClipId) { setPreviewClipUrl(null); return; }
    const clip = voiceoverClips.find((c) => c.id === previewClipId);
    if (!clip) { setPreviewClipId(null); return; }
    if (clip.videoBlob) {
      const url = URL.createObjectURL(clip.videoBlob);
      setPreviewClipUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (clip.videoStoragePath) {
      setPreviewClipLoading(true);
      getDownloadURL(storageRef(storage, clip.videoStoragePath))
        .then((url) => setPreviewClipUrl(url))
        .catch(() => setPreviewClipUrl(null))
        .finally(() => setPreviewClipLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewClipId]);

  // Resolve blob URL for the current lesson preview clip
  useEffect(() => {
    if (!previewLessonOpen) { setPreviewLessonUrl(null); return; }
    const clip = previewableClips[previewLessonIdx];
    if (!clip) { setPreviewLessonUrl(null); return; }
    if (clip.videoBlob) {
      const url = URL.createObjectURL(clip.videoBlob);
      setPreviewLessonUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (clip.videoStoragePath) {
      getDownloadURL(storageRef(storage, clip.videoStoragePath))
        .then((url) => setPreviewLessonUrl(url))
        .catch(() => setPreviewLessonUrl(null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLessonOpen, previewLessonIdx]);

  // ── App chrome suppression ────────────────────────────────────────────────────
  // Belt and braces alongside the portal. The portal is meant to put this
  // overlay outside Layout's subtree so no ancestor stacking context can trap
  // it, but that has proven fragile in practice, so this removes the chrome
  // outright instead of out-painting it. index.css hides #app-top-bar and
  // #mobile-bottom-nav while the attribute is present.
  //
  // Cleanup runs on ANY unmount, so the chrome returns whether the coach used
  // the back control, submitted the lesson, or the parent unmounted the editor
  // some other way.
  useEffect(() => {
    openEditorCount += 1;
    document.body.setAttribute('data-editor-open', 'true');
    return () => {
      openEditorCount -= 1;
      if (openEditorCount <= 0) {
        openEditorCount = 0;
        document.body.removeAttribute('data-editor-open');
      }
    };
  }, []);

  // ── Close with save ───────────────────────────────────────────────────────────
  // Flushes any pending debounced auto-save before calling onCancel so that
  // changes made within the 1.5 s debounce window are not lost on quick close.
  const handleClose = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (onSaveDraft && !isSavingRef.current) {
      try {
        const data = buildDraftData();
        const updated = await onSaveDraft(data);
        lastSavedKeyRef.current = draftKey({
          ...data,
          voiceoverNotableSegmentClips: updated.voiceoverNotableSegmentClips,
        });
        setIsDirty(false);
      } catch {
        // Ignore save errors on close — don't block the user from closing
      }
    }
    onCancel();
  }, [onCancel, onSaveDraft, buildDraftData]);

  // ── Render ────────────────────────────────────────────────────────────────────

  // Portalled to document.body. The editor is mounted deep inside a page, and
  // Layout wraps every page in PageTransition — a motion.div that becomes a
  // stacking context while it animates opacity. That trapped this overlay's
  // z-index inside it, letting the 72px top bar (z-50, a sibling of the whole
  // page in the root context) paint over a z-10000 editor. Portalling puts the
  // editor outside Layout's subtree entirely, so no app chrome can overlap it
  // on either breakpoint, and it clears the z-9999 mobile bottom nav too.
  // Same escape hatch ProgressReportModal already uses.
  return createPortal(
    <div className="fixed inset-0 overflow-hidden" style={{ background: 'var(--editor-bg)', zIndex: 'var(--z-editor-root)' }}>
      <div className="flex flex-col h-full w-full overflow-hidden"
        style={{
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
      <div className="relative flex flex-col flex-1 overflow-hidden">

        {/* ── Header ── */}
        <div className="relative px-4 py-2.5 flex items-center gap-3 flex-shrink-0" style={{ background: 'var(--editor-header)', borderBottom: '1px solid var(--editor-border)' }}>
          {/* Leave the editor. Returns to the lesson detail underneath — this
              overlay is local state, so there is nothing to navigate. Restores
              the previously commented-out Cancel: without it the coach's only
              exit was the browser back gesture, which skips every URL-less
              overlay and pops the last real route (Messages, if that is where
              they came from). handleClose flushes the pending
              auto-save first, so no edits are lost on the way out. */}
          {/* Hidden while the clip editor is open. Two unlabelled controls 60px
              apart, one closing a layer and one leaving the lesson, is the
              dangerous pair; hiding this one also guarantees the two are never
              both on screen at any width. Safe to hide: the clip editor's own
              close is unconditionally setShowClipEditorModal(false), so it
              always returns here. */}
          {/* Conditionally rendered rather than toggled with `hidden`: the
              recipe sets `inline-flex`, and `hidden` beside it is a coin flip
              on which display utility Tailwind emits last. Same result, no
              dependence on stylesheet order. */}
          {!showClipEditorModal && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Back to Lessons"
            title="Back to Lessons"
            /* The control exits to the orders list, so it is "lessons" plural:
               the coach is already inside the lesson. The 44 target now comes
               from the recipe's ::after rather than from min-h, which is what
               lets this row drop from 64 to 56 without losing the target. */
            className={`${EBTN_GHOST} mr-1`}
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span className="hidden md:inline text-sm">Back to Lessons</span>
          </button>
          )}

          {/* Pushes the actions cluster hard right. The title is absolutely
              positioned and out of flow, so it no longer does this itself. */}
          <div className="flex-1" />

          {/* Centred on the header, not on what is left over between the
              clusters: absolute positioning means the title stays put whatever
              the left and right widths do, and max-w keeps it clear of them at
              narrow widths rather than colliding. */}
          <div className="absolute left-1/2 -translate-x-1/2 max-w-[46%] md:max-w-[52%] pointer-events-none">
            {/* One size, one weight, one colour across both halves. Splitting
                the treatment made the type and the name read as two unrelated
                elements sitting next to each other rather than as one title.
                The pill below carries the affordance on its own. */}
            {/* Baseline-aligned, not box-aligned.
                `items-center` lined up the two boxes, and the pill's box is
                taller than the words beside it because it holds a 32px avatar —
                so its text sat below the outer text even though the boxes were
                centred on each other. `items-baseline` aligns type to type.
                A flex container takes its baseline from its first
                *baseline-aligned* item, so the avatar carries `self-center` and
                is skipped; the name becomes the pill's baseline. Neither the
                avatar's size nor the title's line height changes. */}
            <h2 className="text-base md:text-xl font-semibold text-center flex items-baseline justify-center gap-1.5 min-w-0" style={{ color: 'var(--editor-text)' }}>
              {titleFirstName ? (
                <>
                  <span className="truncate">{displayTitle} for</span>
                  <button
                    type="button"
                    ref={playerChipRef}
                    onClick={() => {
                      const r = playerChipRef.current?.getBoundingClientRect();
                      if (r) setPlayerCardAnchor({ x: r.left + r.width / 2, y: r.bottom + 8 });
                      setPlayerCardOpen((v) => !v);
                    }}
                    aria-haspopup="dialog"
                    aria-expanded={playerCardOpen}
                    data-testid="header-player-name"
                    className="pointer-events-auto flex items-baseline gap-2 flex-shrink-0 rounded-full pr-2.5 pl-1 py-1 -my-1 transition-colors hover:bg-[rgba(255,255,255,0.10)]"
                  >
                    {playerAvatarUrl ? (
                      <img
                        src={playerAvatarUrl}
                        alt=""
                        className="w-7 h-7 md:w-8 md:h-8 rounded-full object-cover flex-shrink-0 self-center"
                      />
                    ) : (
                      <span
                        className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center flex-shrink-0 self-center text-xs font-semibold"
                        style={{ background: 'var(--editor-surface)', color: 'var(--editor-text-muted)' }}
                      >
                        {(playerName || titleFirstName).charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="truncate">{titleFirstName}</span>
                  </button>
                </>
              ) : (
                <span className="truncate">{displayTitle}</span>
              )}
            </h2>
            {subtitle && (
              <p className="text-xs text-center truncate" style={{ color: 'var(--editor-text-muted)' }}>{subtitle}</p>
            )}

          </div>

          {/* Auto-save status indicator. "Saved" now means it: a pending edit
              outranks it, so the label cannot reassure a coach who is about to
              close the tab on work that has not been written. */}
          {onSaveDraft && (
            <span className={`text-xs ${
              saveStatus === 'error' ? 'text-[var(--editor-danger)]' :
              saveStatus === 'saving' || isDirty || saveStatus === 'saved'
                ? 'text-[var(--editor-text-muted)]'
                : 'text-transparent'
            }`}>
              {saveStatus === 'error'  ? 'Save failed' :
               saveStatus === 'saving' ? 'Saving…' :
               isDirty                 ? 'Unsaved changes' :
               saveStatus === 'saved'  ? 'Saved' : '·'}
            </span>
          )}

          {/* Organizer toggle */}
          <button
            type="button"
            onClick={() => setOrganizerOpen(prev => !prev)}
            className={organizerOpen ? EBTN_ICON_ACTIVE : EBTN_ICON}
            aria-pressed={organizerOpen}
            title={organizerOpen ? 'Hide lesson plan' : 'Show lesson plan'}
            aria-label={organizerOpen ? 'Hide lesson plan' : 'Show lesson plan'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
            </svg>
          </button>

          {/* Submit — three states: idle / submitting / handing off.
              There is no success state here any more, and that is the point.
              A checkmark reading "Sent" used to appear ~1.2s after the click,
              which was ~1.2s after `onSubmitDraft` resolved — and that resolves
              the moment the submission is handed to the background, before a
              single byte has uploaded. It was telling coaches the lesson had
              landed while the 34-second upload had not started. Lesson
              5DXMbk5z5K1YVvArNVVD was lost to exactly that: told "Sent", closed
              the tab, delivery discarded with no server-side trace.
              The button now keeps spinning and says "Sending", and the only
              thing allowed to claim delivery is the indicator, which reads
              lesson.submissionStatus and therefore outlives this tab. */}
          {onSubmitDraft && (
            <button
              data-testid="lve-submit-btn"
              onClick={() => {
                // Final program lesson: open the progress-report modal
                // instead of the standard submit confirmation. The modal's
                // CTA is what eventually fires the actual lesson submission
                // (paired with the report write).
                if (isFinalProgramLesson && onFinalSubmit) {
                  setProgressReportOpen(true);
                } else {
                  setShowSubmitConfirmation(true);
                }
              }}
              disabled={!hasClips || isSubmitting || submitSuccess}
              /* A full-height filled pill at full opacity, against the recipe's
                 40% disabled. Before this it was a 30px rounded rect at
                 disabled:opacity-50, close enough to its own enabled state on a
                 dark header to read as greyed out when it is live. The success
                 fill is inline so it beats the recipe's background
                 deterministically rather than by emission order. */
              className={EBTN_PRIMARY}
            >
              {submitSuccess ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span className="hidden md:inline">Sending…</span>
                </>
              ) : isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span className="hidden md:inline">Submitting…</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                  <span className="hidden md:inline">
                    {isFinalProgramLesson && onFinalSubmit ? 'Write Report & Submit Lesson' : 'Submit'}
                  </span>
                </>
              )}
            </button>
          )}

          <button type="button" onClick={handleClose} className={EBTN_ICON} aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Toolbar ── */}
        {/* <div className="border-b border-db-border bg-db-bg px-3 py-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            {// Define notable segment //}
            <div className="flex items-center gap-0.5">
              <button
                onClick={handleNotableSegmentBound}
                className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                  notableSegFirstBound !== null
                    ? 'bg-db-accent text-white animate-pulse'
                    : 'text-db-accent hover:text-db-accent hover:bg-db-navy-light'
                }`}
                title={notableSegFirstBound === null
                  ? 'Define notable segment (click to set start)'
                  : 'Click to set end of segment (same point = freeze frame)'}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                </svg>
              </button>
              {notableSegFirstBound !== null && (
                <button
                  onClick={handleCancelNotableSegBound}
                  className="w-8 h-8 flex items-center justify-center rounded text-db-text-3 hover:text-red-500 hover:bg-red-50"
                  title="Cancel segment definition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                    <path strokeLinecap="round" d="M15 9l-6 6M9 9l6 6"/>
                  </svg>
                </button>
              )}
            </div>

            {// Divider //}
            <div className="w-px h-6 bg-gray-300" />

            {// Segment start indicator //}
            {notableSegFirstBound !== null && (
              <span className="text-xs text-db-accent font-mono">
                Start: {formatTime(notableSegFirstBound)}
              </span>
            )}

            {// Mute button (pushed right) //}
            <div className="flex items-center gap-0.5 ml-auto">
              <button
                onClick={handleToggleAudioMute}
                className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                  audioMuted
                    ? 'text-red-500 hover:text-red-600 hover:bg-red-50'
                    : 'text-db-text-3 hover:text-db-text-2 hover:bg-db-bg'
                }`}
                title={audioMuted ? 'Original audio muted (click to unmute)' : 'Original audio on (click to mute)'}
              >
                {audioMuted ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div> */}

        {/* ── Content: video area (flex-1) + organizer panel (fixed width) ── */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden p-0 md:p-3 md:pb-5 gap-0 md:gap-3">

          {/* ── Left: video + timeline + toolbar ── */}
          <div className={`flex flex-col overflow-hidden relative rounded-none md:rounded-xl md:border md:border-[var(--editor-border)] ${organizerOpen ? 'h-[25vh] max-h-[150px] md:h-auto md:max-h-none md:flex-1' : 'flex-1'}`} style={{ background: 'var(--editor-bg)' }}>
            {/* Video player — fills all available space, black letterbox */}
            <div className="flex-1 min-h-0 bg-black overflow-hidden">
              <VideoEditorPlayer
                ref={videoPlayerRef}
                videoUrl={videoUrl}
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={setDuration}
                onPlayStateChange={setIsPlaying}
                onTogglePlay={handleTogglePlay}
                audioMuted={audioMuted}
                rotation={videoRotation}
                fillHeight
              />
            </div>

            {/* Timeline + controls row */}
            <div className="flex-shrink-0 flex items-center gap-3 px-4" style={{ height: '56px', background: 'var(--editor-timeline)', borderTop: '1px solid var(--editor-border)' }}>
              <div className="flex-1 min-w-0">
                <Timeline
                  duration={duration}
                  currentTime={currentTime}
                  slowMotionSegments={notableSegments}
                  playIntervals={lessonRequest?.playerVideoPlayIntervals}
                  onCreateHighlightFromInterval={handleCreateHighlightFromInterval}
                  isPlaying={isPlaying}
                  onSeek={handleSeek}
                  onScrubStart={beginTransportScrub}
                  onScrubEnd={endTransportScrub}
                  onTogglePlay={handleTogglePlay}
                  onUpdateSloMoSegmentEndTime={handleUpdateNotableSegmentEndTime}
                />
              </div>
              <div className="flex-none flex items-center gap-2">
                {/* ±5s skip buttons */}
                <button
                  onClick={() => handleSeek(Math.max(0, currentTime - 5))}
                  className={EBTN_ICON}
                  title="Rewind 5 seconds"
                  aria-label="Rewind 5 seconds"
                >
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                    <text x="7" y="15.5" fontSize="6" fontWeight="bold" fill="currentColor">5</text>
                  </svg>
                </button>
                <button
                  onClick={() => handleSeek(Math.min(duration, currentTime + 5))}
                  className={EBTN_ICON}
                  title="Forward 5 seconds"
                  aria-label="Forward 5 seconds"
                >
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
                    <text x="8" y="15.5" fontSize="6" fontWeight="bold" fill="currentColor">5</text>
                  </svg>
                </button>
                <div className="w-px h-6" style={{ background: 'var(--editor-border)' }} />
                {/* Capture the last 15 seconds */}
                <button
                  data-testid="highlight-tool-btn"
                  data-current-time={currentTime}
                  onClick={handleCaptureSegment}
                  className={EBTN_ICON}
                  style={{ color: 'var(--color-biscayne)' }}
                  title="Save the last 15 seconds"
                  aria-label="Save the last 15 seconds"
                >
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                  </svg>
                </button>
                {/* Divider */}
                <div className="w-px h-6" style={{ background: 'var(--editor-border)' }} />
                {/* Audio mute */}
                <button
                  onClick={handleToggleAudioMute}
                  className={EBTN_ICON}
                  /* Inline, per the recipe: a second arbitrary-value text
                     colour class would resolve by emission order, not by
                     class order. */
                  style={audioMuted ? { color: 'var(--editor-danger)' } : undefined}
                  title={audioMuted ? 'Original audio muted (click to unmute)' : 'Original audio on (click to mute)'}
                  aria-label={audioMuted ? 'Unmute' : 'Mute'}
                >
                  {audioMuted ? (
                    <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                    </svg>
                  ) : (
                    <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  )}
                </button>
                {/* Divider */}
                <div className="w-px h-6" style={{ background: 'var(--editor-border)' }} />
                {/* Rotate video clockwise */}
                <button
                  onClick={() => setVideoRotation(prev => ((prev + 90) % 360) as 0 | 90 | 180 | 270)}
                  className={EBTN_ICON}
                  style={videoRotation !== 0 ? { color: 'var(--color-db-oasis)' } : undefined}
                  title={videoRotation !== 0 ? `Video rotated ${videoRotation}° (click to rotate more)` : 'Rotate video 90° clockwise'}
                  aria-label="Rotate video clockwise"
                >
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45zM19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02zm-3.04 5.47l1.42 1.42c1.16-1.16 1.71-2.5 1.62-3.89h-2.02c-.14.87-.48 1.72-1.02 2.47z"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* ── Embedded Clip Editor ──
                Inset with a scrim behind it, so it reads as one level deeper
                rather than as the same dark surface with a new label. The
                scrim uses bg-black at an opacity, matching every other modal
                in this editor, rather than a token: there is no --editor-*
                value for a scrim and the six existing overlays already set
                this convention. */}
            {showClipEditorModal && clipEditorPrimarySource && (
              <div className="absolute inset-0 z-[40] bg-black/60 p-1 md:p-2">
                <ClipEditorModal
                  embedded
                  isOpen={showClipEditorModal}
                  onClose={() => setShowClipEditorModal(false)}
                  onSave={handleSaveClip}
                  onSaveAndContinue={handleSaveClip}
                  primarySource={clipEditorPrimarySource}
                  secondarySource={clipEditorSecondarySource ?? undefined}
                  /* Passed unconditionally now. With no second source the
                     control adds one; with one it swaps it, which is the only
                     way a coach could change a comparison at all. */
                  onAddSecondarySource={handleAddSecondarySourceToClipEditor}
                />
              </div>
            )}

          </div>

          {/* Mobile toggle bar */}
          <button
            data-testid="lesson-organizer-toggle"
            onClick={() => setOrganizerOpen(prev => !prev)}
            className="flex md:hidden items-center justify-center gap-2 flex-shrink-0 w-full"
            style={{ height: '40px', background: 'var(--editor-sidebar)', borderTop: '1px solid var(--editor-border)' }}
          >
            <span className="text-[13px] font-medium" style={{ color: 'var(--editor-text-muted)' }}>Lesson Plan</span>
            <svg
              className="w-4 h-4 transition-transform"
              style={{ color: 'var(--editor-text-muted)', transform: organizerOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* ── Right: Lesson Organizer ── */}
          {/* Hidden file input for upload side-by-side */}
          <input
            ref={uploadFileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file || !pendingUploadSegmentIdRef.current) return;
              const segId = pendingUploadSegmentIdRef.current;
              pendingUploadSegmentIdRef.current = null;
              e.target.value = '';
              const primarySeg = notableSegments.find((s) => s.id === segId);
              if (!primarySeg) return;
              const blobUrl = URL.createObjectURL(file);
              setClipEditorPrimarySource({ videoUrl: videoUrl ?? '', segment: primarySeg, label: primarySeg.label, rotation: videoRotation || undefined });
              setClipEditorSecondarySource({ videoUrl: blobUrl, label: file.name.replace(/\.[^.]+$/, '') });
              setShowClipEditorModal(true);
            }}
          />
          {organizerOpen && (
            <div className="flex-1 w-full md:flex-none md:w-[320px] overflow-y-auto flex-shrink-0 min-h-0">
              <LessonOrganizerPanel
                clips={voiceoverClips}
                notableSegments={notableSegments}
                notes={notes}
                onNotesChange={setNotes}
                coachNotes={coachNotes}
                onCoachNotesChange={setCoachNotes}
                onAddRecording={handleAddRecordingForSegment}
                onAddSideBySide={handleAddSideBySideForSegment}
                onDeleteClip={handleDeleteClip}
                onDeleteSegment={handleDeleteNotableSegment}
                onPlaySegment={handlePlaySegment}
                onRenameSegment={(segId, newLabel) => handleUpdateSegment(segId, { label: newLabel })}
                onReorderSegments={handleReorderSegments}
                attachedDrills={attachedDrills}
                onToggleDrill={handleToggleDrill}
                coachId={currentUser?.uid ?? ''}
                onPreviewClip={setPreviewClipId}
                onPreviewLesson={() => { setPreviewLessonIdx(0); setPreviewLessonOpen(true); }}
                onRenameClip={handleRenameClip}
                hasPlayerLibrary={!!playerId}
                offeringType={offeringType}
              />
            </div>
          )}
        </div>

      </div>

      {/* ── Segment naming modal ── */}
      {/* ── Player details ──
          A child of the editor root, not of the header.
          It used to sit inside the title container, which carries
          -translate-x-1/2. A transform creates a stacking context, so the
          popover's z-index was scoped inside it and could not rise above the
          video pane: the pane and the header are both positioned at z-auto in
          the root's context, and the pane comes later in the DOM, so it painted
          on top. Moving the popover here puts it after the pane in DOM order
          inside the same z-[9999] root, which is the fix; the z-index below only
          settles it against its immediate siblings.

          Deliberately NOT portalled to document.body. Four portals in this
          editor already render behind the root and are invisible. */}
      {playerCardOpen && playerCardAnchor && (
        <div
          ref={playerCardRef}
          role="dialog"
          aria-label={`${playerName || titleFirstName} details`}
          data-testid="header-player-card"
          className="absolute z-50 w-60 -translate-x-1/2 rounded-xl p-4 shadow-2xl text-left"
          style={{
            left: playerCardAnchor.x,
            top: playerCardAnchor.y,
            background: 'var(--editor-sidebar)',
            border: '1px solid var(--editor-border)',
          }}
        >
          <div className="flex items-center gap-3">
            {playerAvatarUrl ? (
              <img src={playerAvatarUrl} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold"
                style={{ background: 'var(--editor-surface)', color: 'var(--editor-text-muted)' }}
              >
                {(playerName || titleFirstName || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: 'var(--editor-text)' }}>
                {playerName || titleFirstName}
              </div>
              {playerUtr != null && (
                <div className="text-xs" style={{ color: 'var(--editor-text-muted)' }}>UTR {playerUtr}</div>
              )}
            </div>
          </div>
          {playerGoal && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--editor-border)' }}>
              <div className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--editor-text-muted)' }}>Goal</div>
              <div className="mt-0.5 text-xs" style={{ color: 'var(--editor-text)' }}>{playerGoal}</div>
            </div>
          )}
          {playerUtr == null && !playerGoal && (
            <div className="mt-3 text-xs" style={{ color: 'var(--editor-text-muted)' }}>
              No UTR or goal on file.
            </div>
          )}
        </div>
      )}

      {/* ── Segment capture confirmation ── */}
      {pendingSegmentBounds && (
        <SegmentCaptureModal
          captureStart={pendingSegmentBounds.startTime}
          captureEnd={pendingSegmentBounds.endTime}
          duration={duration}
          defaultName={segmentNameInput}
          onSeek={(t) => videoPlayerRef.current?.seek(t)}
          onSave={handleSaveCapture}
          onCancel={handleCancelCapture}
          position={capturePanelPos}
          onPositionChange={setCapturePanelPos}
          organizerOpen={organizerOpen}
        />
      )}

      {/* ── Notable segment delete confirmation ── */}
      {showNotableSegDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
          <div className="rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>
            <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--editor-text)' }}>Delete highlight?</h3>
            <p className="mb-6 text-sm" style={{ color: 'var(--editor-text-muted)' }}>
              This segment is referenced by {showNotableSegDeleteConfirm.clipCount} recording{showNotableSegDeleteConfirm.clipCount > 1 ? 's' : ''}. Deleting it will also delete those recordings.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowNotableSegDeleteConfirm(null)}
                className={EBTN_SECONDARY}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteNotableSegment}
                className={EBTN_DESTRUCTIVE}
              >
                Delete Highlight &amp; Recordings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add comparison source modal ── */}
      {showComparisonSourceModal && clipEditorPrimarySource && (
        <AddComparisonSourceModal
          isOpen={showComparisonSourceModal}
          defaultTab={comparisonDefaultTab}
          onClose={() => {
            setShowComparisonSourceModal(false);
            setComparisonDefaultTab(undefined);
            openClipEditorAfterComparisonRef.current = false;
          }}
          onConfirm={(source) => {
            setClipEditorSecondarySource(source);
            setShowComparisonSourceModal(false);
            setComparisonDefaultTab(undefined);
            if (openClipEditorAfterComparisonRef.current) {
              openClipEditorAfterComparisonRef.current = false;
              setShowClipEditorModal(true);
            }
          }}
          primarySource={clipEditorPrimarySource}
          videoUrl={videoUrl}
          notableSegments={notableSegments}
          playerId={playerId}
          originalVideoId={originalVideoId}
          videoRotation={videoRotation || undefined}
        />
      )}

      {/* ── Submit confirmation modal ── */}
      {showSubmitConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
          <div className="rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>
            {isSubmitting || submitSuccess ? (
              <div className="flex flex-col items-center text-center py-2">
                <div
                  className="animate-spin rounded-full h-8 w-8 mb-4"
                  style={{ borderBottom: '2px solid var(--editor-text)' }}
                />
                <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--editor-text)' }}>
                  Sending your lesson
                </h3>
              </div>
            ) : (
            <>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--editor-text)' }}>Submit Lesson?</h3>
            {/* Not "view your lesson recordings immediately after submission",
                for two reasons worth keeping written down.
                "Immediately" is a promise the processing pipeline cannot always
                keep — Cloud Run transcodes the composition after the submit
                returns, and a long breakdown is minutes behind the click.
                "Recordings" is our internal word for the clips inside the
                editor; the player is shown "breakdown" on every screen they
                see, so a coach reading this and a player reading their lesson
                were being given two names for one thing.
                What replaced it is the fact that matters at this button: it is
                one-way. */}
            <p className="mb-6" style={{ color: 'var(--editor-text-muted)' }}>
              This sends your breakdown to {titleFirstName || 'the player'}. You won&apos;t be able to change it afterwards.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSubmitConfirmation(false)}
                disabled={isSubmitting}
                className={EBTN_SECONDARY}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!onSubmitDraft) return;
                  setIsSubmitting(true);
                  /* The dialog deliberately stays open. It swaps to the handoff
                     state below rather than vanishing, because this is the one
                     moment the coach is certainly looking at the screen and the
                     one sentence that matters — keep this tab open — has to be
                     somewhere they will read it. */
                  try {
                    await onSubmitDraft(buildDraftData());
                    setIsSubmitting(false);
                    setSubmitSuccess(true);
                    /* 600ms, down from 1200. It was sized to let a coach read
                       "Sent"; there is nothing to read now, and the handoff copy
                       lives in the dialog rather than this flash. */
                    setTimeout(() => {
                      setShowSubmitConfirmation(false);
                      onSubmitSuccess?.(lessonRequest?.id ?? lessonId ?? '');
                      setSubmitSuccess(false);
                    }, 600);
                  } catch (error) {
                    showError('Failed to submit lesson');
                    console.error('Submit draft error:', error);
                    setIsSubmitting(false);
                    // The dialog no longer closes on confirm, so close it here.
                    setShowSubmitConfirmation(false);
                  }
                }}
                disabled={isSubmitting}
                className={EBTN_PRIMARY}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Submitting...
                  </>
                ) : (
                  'Submit Lesson'
                )}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}
      {/* ── Clip preview modal ── */}
      {previewClipId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80" onClick={() => setPreviewClipId(null)}>
          <div className="rounded-xl overflow-hidden w-full max-w-2xl mx-4 shadow-2xl" style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--editor-border)' }}>
              <h3 className="text-sm font-semibold text-[var(--editor-text)]">
                {voiceoverClips.find((c) => c.id === previewClipId)?.label ?? 'Clip Preview'}
              </h3>
              <button onClick={() => setPreviewClipId(null)} className="text-[var(--editor-text-muted)] hover:text-[var(--editor-text)]" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="bg-black" style={{ minHeight: 200 }}>
              {previewClipLoading ? (
                <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-db-oasis)]"/></div>
              ) : previewClipUrl ? (
                <video src={previewClipUrl} controls autoPlay playsInline className="w-full max-h-[60vh] object-contain" />
              ) : (
                <div className="flex items-center justify-center py-16 text-sm text-[var(--editor-text-muted)]">Preview not available</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Lesson preview modal ── */}
      {previewLessonOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80" onClick={() => setPreviewLessonOpen(false)}>
          <div className="rounded-xl overflow-hidden w-full max-w-2xl mx-4 shadow-2xl flex flex-col" style={{ maxHeight: '85vh', background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderBottom: '1px solid var(--editor-border)' }}>
              <div>
                <h3 className="text-sm font-semibold text-[var(--editor-text)]">Lesson Preview</h3>
                {previewableClips.length > 0 && (
                  <p className="text-xs text-[var(--editor-text-muted)] mt-0.5">
                    Clip {previewLessonIdx + 1} of {previewableClips.length}
                    {previewableClips[previewLessonIdx]?.label ? ` · ${previewableClips[previewLessonIdx].label}` : ''}
                  </p>
                )}
              </div>
              <button onClick={() => setPreviewLessonOpen(false)} className="text-[var(--editor-text-muted)] hover:text-[var(--editor-text)]" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="bg-black flex-1 min-h-0">
              {previewableClips.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-sm text-[var(--editor-text-muted)]">No recorded clips to preview</div>
              ) : previewLessonUrl ? (
                <video
                  key={previewLessonUrl}
                  src={previewLessonUrl}
                  controls
                  autoPlay
                  playsInline
                  className="w-full max-h-[55vh] object-contain"
                  onEnded={() => {
                    if (previewLessonIdx < previewableClips.length - 1) setPreviewLessonIdx((i) => i + 1);
                  }}
                />
              ) : (
                <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-db-oasis)]"/></div>
              )}
            </div>
            {previewableClips.length > 1 && (
              <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderTop: '1px solid var(--editor-border)' }}>
                <button
                  onClick={() => setPreviewLessonIdx((i) => Math.max(0, i - 1))}
                  disabled={previewLessonIdx === 0}
                  className="px-3 py-1.5 rounded-lg text-sm disabled:opacity-40 text-[var(--editor-text)] hover:bg-[var(--editor-hover)] disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
                  Previous
                </button>
                <div className="flex gap-1.5">
                  {previewableClips.map((_, i) => (
                    <button key={i} onClick={() => setPreviewLessonIdx(i)} className={`w-2 h-2 rounded-full transition-colors ${i === previewLessonIdx ? 'bg-[var(--color-db-oasis)]' : 'bg-[var(--editor-border)] hover:bg-[var(--editor-hover-strong)]'}`} aria-label={`Clip ${i + 1}`} />
                  ))}
                </div>
                <button
                  onClick={() => setPreviewLessonIdx((i) => Math.min(previewableClips.length - 1, i + 1))}
                  disabled={previewLessonIdx === previewableClips.length - 1}
                  className="px-3 py-1.5 rounded-lg text-sm disabled:opacity-40 text-[var(--editor-text)] hover:bg-[var(--editor-hover)] disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Final-lesson progress report modal ──
          Opened when the coach hits "Write Report & Submit Lesson" on the
          final program lesson. Controlled component — draft state lives in
          this editor so closing/reopening (X with confirm, Esc with confirm,
          or Save draft) preserves the answers across the editor session. */}
      {progressReportOpen && isFinalProgramLesson && onFinalSubmit && (
        <ProgressReportModal
          playerFirstName={playerFirstName?.trim() || 'player'}
          totalLessons={totalLessons ?? 4}
          playerGoal={playerGoal}
          draft={progressReportDraft}
          onDraftChange={setProgressReportDraft}
          onClose={() => setProgressReportOpen(false)}
          onSubmit={async () => {
            // Lifecycle: modal stays mounted with its own "Submitting…"
            // spinner until this resolves. Editor's outer isSubmitting flag
            // is also flipped so the header Submit button reflects state.
            setIsSubmitting(true);
            try {
              await onFinalSubmit({
                draftData: buildDraftData(),
                report: {
                  whatImproved: progressReportDraft.whatImproved.trim(),
                  keepDoing: progressReportDraft.keepDoing.trim(),
                  nextFocus: progressReportDraft.nextFocus.trim(),
                  personalNote: progressReportDraft.personalNote.trim(),
                },
              });
              // Resolve = lesson submit succeeded (parent owns toast/navigation).
              // Close the modal and clear the draft.
              setProgressReportDraft({ whatImproved: '', keepDoing: '', nextFocus: '', personalNote: '' });
              setProgressReportOpen(false);
              setSubmitSuccess(true);
              setTimeout(() => onSubmitSuccess?.(lessonRequest?.id ?? '', playerFirstName), 1200);
            } catch (err) {
              // Lesson submit failed — modal stays open so the coach can retry.
              setIsSubmitting(false);
              throw err;
            }
          }}
        />
      )}
      </div>
    </div>,
    document.body,
  );
}
